import { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { thumbUrl } from "@/lib/media";

export const CartDrawer = ({ isOpen, onClose }) => {
  const { items, removeItem, updateQuantity, itemCount, subtotal, clearCart } = useCart();

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
        data-testid="cart-backdrop"
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md z-50 bg-white shadow-xl animate-fade-in">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-stone-200">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-deep-moss" />
              <h2 className="font-serif text-xl font-semibold text-stone-900">
                Your Cart ({itemCount})
              </h2>
            </div>
            <button
              onClick={onClose}
              data-testid="close-cart-btn"
              className="p-2 text-stone-500 hover:text-stone-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-6">
            {items.length === 0 ? (
              <div className="text-center py-12">
                <ShoppingCart className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                <p className="font-sans text-stone-500">Your cart is empty</p>
                <button
                  onClick={onClose}
                  className="mt-4 text-deep-moss font-sans text-sm hover:underline"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {items.map((item) => (
                  <div
                    key={item.product_id}
                    className="flex gap-4"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={thumbUrl(item.image_url)}
                      alt={item.name}
                      className="w-20 h-20 object-cover border border-stone-200"
                    />
                    <div className="flex-1">
                      <h3 className="font-sans text-sm font-medium text-stone-900">
                        {item.name}
                      </h3>
                      <p className="font-sans text-sm text-deep-moss font-semibold mt-1">
                        ${item.price.toFixed(2)}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <div className="flex items-center border border-stone-200">
                          <button
                            onClick={() =>
                              updateQuantity(item.product_id, item.quantity - 1)
                            }
                            data-testid={`decrease-qty-${item.product_id}`}
                            className="p-1 hover:bg-stone-100"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="px-3 font-sans text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(item.product_id, item.quantity + 1)
                            }
                            data-testid={`increase-qty-${item.product_id}`}
                            className="p-1 hover:bg-stone-100"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          data-testid={`remove-item-${item.product_id}`}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="font-sans text-sm font-semibold text-stone-900">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {items.length > 0 && (
            <div className="border-t border-stone-200 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <span className="font-sans text-stone-600">Subtotal</span>
                <span className="font-sans text-lg font-semibold text-stone-900">
                  ${subtotal.toFixed(2)}
                </span>
              </div>
              <p className="font-sans text-xs text-stone-500">
                Shipping calculated at checkout
              </p>
              <Link
                to="/checkout"
                onClick={onClose}
                data-testid="checkout-btn"
                className="block w-full bg-deep-moss hover:bg-deep-moss/90 text-white text-center rounded-full px-6 py-3 font-sans font-medium transition-colors"
              >
                Proceed to Checkout
              </Link>
              <button
                onClick={clearCart}
                data-testid="clear-cart-btn"
                className="w-full text-center text-stone-500 font-sans text-sm hover:text-stone-700"
              >
                Clear Cart
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
